/**
 * PdfTemplateEditor — Éditeur visuel drag & drop pour mapper les signatures
 * sur les pages PDF d'un template (Bail TAL, etc.).
 *
 * Route : /admin/pdf-templates/:id
 *
 * Architecture :
 *  - Image PNG de chaque page rendue par pdftoppm côté backend (GET .../page/{n})
 *  - Étiquettes = divs `absolute` avec custom drag handler (onMouseDown/Move/Up)
 *  - Sidebar : palette d'étiquettes prédéfinies (cliquer pour ajouter au centre)
 *  - Coordonnées en POINTS PDF (origine top-left), conversion via ratio image_displayed / pdf
 *
 * Extrait de PdfTemplatesPage.jsx (Batch #14.221 — refactor oversized 2026-02).
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import { ArrowLeft, Save, Eye, Trash2, Loader2, X, ZoomIn, ZoomOut, MoveHorizontal, Plus } from "lucide-react";

// ===========================================================================
// Éditeur visuel — Route /admin/pdf-templates/:id
// ===========================================================================
export function PdfTemplateEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const { setTrail } = useBreadcrumb();
  useEffect(() => {
    if (!template) return undefined;
    setTrail([
      { label: "Centre de communications", href: "/admin/documents" },
      { label: "Documents à signer", href: "/admin/client-documents" },
      { label: "Modèles PDF", href: "/admin/client-documents?tab=modeles" },
      { label: template.name || "Modèle" },
    ]);
    return () => setTrail(null);
  }, [template, setTrail]);
  const [labels, setLabels] = useState([]); // palette
  const [fields, setFields] = useState([]); // zones placées
  const [currentPage, setCurrentPage] = useState(1);
  const [zoom, setZoom] = useState(1.0);
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pageImageVersion, setPageImageVersion] = useState(0);
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const [imageNaturalDims, setImageNaturalDims] = useState({ width: 612, height: 1008 });

  useEffect(() => {
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          api.get(`/admin/pdf-templates/${id}`),
          api.get("/admin/pdf-templates/predefined-labels"),
        ]);
        setTemplate(r1.data);
        setFields(r1.data.fields || []);
        setLabels(r2.data?.labels || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Template introuvable");
        navigate("/admin/client-documents?tab=modeles");
      }
    })();
  }, [id, navigate]);

  if (!template) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  // Dimensions PDF (référence pour conversion coords)
  const pdfW = template.page_dimensions?.[currentPage - 1]?.width || 612;
  const pdfH = template.page_dimensions?.[currentPage - 1]?.height || 1008;

  // Image rendered size = imageNaturalDims × zoom
  // PDF coord → pixel coord : x_px = x_pt × (imageDisplayedWidth / pdf_W)
  const displayedW = imageNaturalDims.width * zoom;
  const displayedH = imageNaturalDims.height * zoom;
  const ptToPx = displayedW / pdfW;

  const addFieldFromLabel = (label) => {
    const newField = {
      id: `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      label: label.label,
      page: currentPage,
      // Centre par défaut, en POINTS PDF
      x: pdfW / 2 - (label.default_w || 200) / 2,
      y: pdfH / 2 - (label.default_h || 40) / 2,
      width: label.default_w || 200,
      height: label.default_h || 40,
      signer_role: label.signer_role,
      signer_index: label.signer_index,
      type: label.type,
    };
    setFields((arr) => [...arr, newField]);
    setSelectedFieldId(newField.id);
  };

  const deleteField = (fid) => {
    setFields((arr) => arr.filter((f) => f.id !== fid));
    if (selectedFieldId === fid) setSelectedFieldId(null);
  };

  const updateField = (fid, patch) => {
    setFields((arr) => arr.map((f) => f.id === fid ? { ...f, ...patch } : f));
  };

  // Drag handler — capture mouse, computes delta in PIXELS, converts back to POINTS
  const onFieldMouseDown = (e, fid) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedFieldId(fid);
    const field = fields.find((f) => f.id === fid);
    if (!field) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const startFieldX = field.x;
    const startFieldY = field.y;

    const onMove = (ev) => {
      const dxPx = ev.clientX - startX;
      const dyPx = ev.clientY - startY;
      const dxPt = dxPx / ptToPx;
      const dyPt = dyPx / ptToPx;
      const newX = Math.max(0, Math.min(pdfW - field.width, startFieldX + dxPt));
      const newY = Math.max(0, Math.min(pdfH - field.height, startFieldY + dyPt));
      updateField(fid, { x: newX, y: newY });
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const onResize = (e, fid, corner) => {
    e.preventDefault();
    e.stopPropagation();
    const field = fields.find((f) => f.id === fid);
    if (!field) return;
    const startX = e.clientX;
    const startW = field.width;
    const startH = field.height;
    const onMove = (ev) => {
      const dxPt = (ev.clientX - startX) / ptToPx;
      const newW = Math.max(20, startW + dxPt);
      const newH = newW * (startH / startW); // garde le ratio
      updateField(fid, { width: newW, height: newH });
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Clean les IDs temporaires
      const cleanFields = fields.map((f) => {
        const out = { ...f };
        if (out.id?.startsWith("tmp_")) delete out.id;
        return out;
      });
      const r = await api.put(`/admin/pdf-templates/${id}`, { fields: cleanFields });
      setFields(r.data?.fields || []);
      toast.success(`${cleanFields.length} zones enregistrées`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const openTestRender = async () => {
    try {
      const token = localStorage.getItem("token");
      const url = `${import.meta.env.VITE_BACKEND_URL || process.env.REACT_APP_BACKEND_URL}/api/admin/pdf-templates/${id}/test-render`;
      const r = await fetch(url, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("Erreur");
      const blob = await r.blob();
      const objUrl = URL.createObjectURL(blob);
      window.open(objUrl, "_blank");
    } catch (_e) {
      toast.error("Impossible d'ouvrir l'aperçu");
    }
  };

  const fieldsCurrentPage = fields.filter((f) => f.page === currentPage);

  const colorByRole = (role, type) => {
    if (type === "text") {
      return { bg: "bg-amber-100/70", border: "border-amber-500", text: "text-amber-900" };
    }
    if (type === "initials") {
      // Batch #13.2 — Initiales en violet pour les distinguer visuellement
      return role === "tenant"
        ? { bg: "bg-violet-100/70", border: "border-violet-500", text: "text-violet-900" }
        : { bg: "bg-fuchsia-100/70", border: "border-fuchsia-500", text: "text-fuchsia-900" };
    }
    return role === "tenant"
      ? { bg: "bg-blue-100/70", border: "border-blue-500", text: "text-blue-900" }
      : { bg: "bg-emerald-100/70", border: "border-emerald-500", text: "text-emerald-900" };
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col" data-testid="pdf-template-editor">
      {/* Header */}
      <div className="bg-white border-b px-4 py-2 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate("/admin/client-documents?tab=modeles")} className="p-1.5 rounded hover:bg-slate-100">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="min-w-0">
            <h1 className="font-bold text-slate-800 text-sm truncate">{template.name}</h1>
            <p className="text-[10px] text-slate-500 font-mono">{template.key} · {template.page_count} pages · {fields.length} zones</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
            <button onClick={() => setZoom((z) => Math.max(0.5, z - 0.1))} className="p-1 hover:bg-white rounded"><ZoomOut className="w-3.5 h-3.5" /></button>
            <span className="text-[11px] font-mono px-1.5 min-w-[40px] text-center">{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom((z) => Math.min(2.0, z + 0.1))} className="p-1 hover:bg-white rounded"><ZoomIn className="w-3.5 h-3.5" /></button>
          </div>
          <button onClick={openTestRender} className="px-3 py-1.5 rounded-lg bg-white border text-xs font-semibold hover:bg-slate-50 inline-flex items-center gap-1.5" data-testid="pdf-template-preview">
            <Eye className="w-3.5 h-3.5" /> Aperçu
          </button>
          <button onClick={save} disabled={saving} className="px-4 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-semibold disabled:opacity-40 inline-flex items-center gap-1.5" data-testid="pdf-template-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Enregistrer
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar palette */}
        <aside className="w-64 bg-white border-r p-3 overflow-y-auto" data-testid="pdf-template-palette">
          <h3 className="text-[10px] uppercase font-bold text-slate-600 tracking-wider mb-2">Palette d'étiquettes</h3>
          <p className="text-[10px] text-slate-500 mb-3">Cliquez pour ajouter au centre de la page courante, puis glissez pour positionner.</p>
          <div className="space-y-2">
            {labels.map((lbl) => {
              const c = colorByRole(lbl.signer_role, lbl.type);
              return (
                <button
                  key={lbl.label}
                  onClick={() => addFieldFromLabel(lbl)}
                  className={`w-full text-left px-2.5 py-2 rounded-lg border-2 ${c.bg} ${c.border} ${c.text} hover:scale-[1.02] transition text-[11px]`}
                  data-testid={`pdf-template-palette-${lbl.signer_role}-${lbl.signer_index}-${lbl.type}`}
                >
                  <div className="flex items-center gap-1.5">
                    <Plus className="w-3 h-3" />
                    <span className="font-semibold">{lbl.label}</span>
                  </div>
                  <div className="text-[9px] mt-0.5 opacity-60">{lbl.type} · {lbl.default_w}×{lbl.default_h}pt</div>
                </button>
              );
            })}
          </div>

          {/* Liste des zones de la page courante */}
          <h3 className="text-[10px] uppercase font-bold text-slate-600 tracking-wider mt-5 mb-2">
            Zones sur cette page ({fieldsCurrentPage.length})
          </h3>
          <div className="space-y-1">
            {fieldsCurrentPage.length === 0 && (
              <p className="text-[10px] italic text-slate-400">Aucune zone sur la page {currentPage}.</p>
            )}
            {fieldsCurrentPage.map((f) => {
              const c = colorByRole(f.signer_role, f.type);
              return (
                <div
                  key={f.id}
                  onClick={() => setSelectedFieldId(f.id)}
                  className={`p-1.5 rounded border ${selectedFieldId === f.id ? "border-slate-800 bg-yellow-50" : `${c.bg} border-transparent`} text-[10px] cursor-pointer flex items-center justify-between gap-1`}
                >
                  <div className="min-w-0 flex-1 truncate">
                    <div className="font-semibold truncate">{f.label}</div>
                    <div className="text-[9px] font-mono opacity-60">x:{Math.round(f.x)} y:{Math.round(f.y)} · {Math.round(f.width)}×{Math.round(f.height)}</div>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); deleteField(f.id); }} className="p-0.5 rounded hover:bg-rose-100 text-rose-500">
                    <Trash2 className="w-2.5 h-2.5" />
                  </button>
                </div>
              );
            })}
          </div>

          {/* Batch #8.1 — Éditeur de field texte personnalisé */}
          <CustomTextEditor
            field={fields.find((f) => f.id === selectedFieldId)}
            onUpdate={(patch) => selectedFieldId && updateField(selectedFieldId, patch)}
          />
        </aside>

        {/* Page navigation + image */}
        <main className="flex-1 overflow-auto bg-slate-200 p-4 relative">
          {/* Page tabs */}
          <div className="sticky top-0 z-10 bg-slate-200 pb-2 mb-2 flex flex-wrap items-center gap-1" data-testid="pdf-template-page-tabs">
            {Array.from({ length: template.page_count }, (_, i) => i + 1).map((p) => {
              const onPageCount = fields.filter((f) => f.page === p).length;
              return (
                <button
                  key={p}
                  onClick={() => setCurrentPage(p)}
                  className={`px-2.5 py-1 rounded text-xs font-semibold transition ${
                    currentPage === p ? "bg-slate-800 text-white" : "bg-white text-slate-700 hover:bg-slate-100"
                  }`}
                  data-testid={`pdf-template-page-${p}`}
                >
                  Page {p}{onPageCount > 0 && (
                    <span className={`ml-1 px-1 rounded text-[9px] ${currentPage === p ? "bg-white/30" : "bg-cyan-100 text-cyan-800"}`}>
                      {onPageCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* PDF page image with overlay fields */}
          <div className="flex justify-center" ref={containerRef}>
            <div
              className="relative shadow-lg bg-white"
              style={{ width: displayedW, height: displayedH }}
              data-testid="pdf-template-canvas"
            >
              <PageImage
                templateId={id}
                pageNum={currentPage}
                version={pageImageVersion}
                onLoad={(dims) => setImageNaturalDims(dims)}
                imageRef={imageRef}
              />

              {/* Fields overlay */}
              {fieldsCurrentPage.map((f) => {
                const c = colorByRole(f.signer_role, f.type);
                const isSelected = selectedFieldId === f.id;
                return (
                  <div
                    key={f.id}
                    onMouseDown={(e) => onFieldMouseDown(e, f.id)}
                    className={`absolute group ${c.bg} ${c.border} ${c.text} border-2 cursor-move flex items-center justify-center text-[10px] font-semibold select-none transition ${isSelected ? "ring-2 ring-yellow-500 z-10" : ""}`}
                    style={{
                      left: f.x * ptToPx,
                      top: f.y * ptToPx,
                      width: f.width * ptToPx,
                      height: f.height * ptToPx,
                    }}
                    data-testid={`pdf-template-field-${f.id}`}
                    title={`${f.label} — drag to move`}
                  >
                    <span className="truncate px-1 pointer-events-none">{f.label}</span>
                    {/* Resize handle bottom-right */}
                    <div
                      onMouseDown={(e) => onResize(e, f.id, "br")}
                      className="absolute bottom-0 right-0 w-3 h-3 bg-yellow-400 cursor-se-resize opacity-0 group-hover:opacity-100"
                      title="Redimensionner"
                    >
                      <MoveHorizontal className="w-3 h-3" />
                    </div>
                    {/* Delete button on hover */}
                    {isSelected && (
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteField(f.id); }}
                        className="absolute -top-3 -right-3 w-5 h-5 rounded-full bg-rose-500 text-white text-[10px] flex items-center justify-center hover:bg-rose-600"
                        data-testid={`pdf-template-field-delete-${f.id}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Help footer */}
          <div className="mt-3 text-center text-[10px] text-slate-500">
            <p><kbd className="px-1 bg-white border rounded">Cliquer</kbd> sur la palette pour ajouter · <kbd className="px-1 bg-white border rounded">Glisser</kbd> pour déplacer · <kbd className="px-1 bg-white border rounded">×</kbd> pour supprimer · <kbd className="px-1 bg-white border rounded">Aperçu</kbd> pour voir le rendu</p>
          </div>
        </main>
      </div>
    </div>
  );
}




// ---------------------------------------------------------------------------
// Batch #8.1 — Éditeur de field texte personnalisé (apparait si selected.type=='text')
// ---------------------------------------------------------------------------
const PLACEHOLDERS = [
  { key: "{tenant_name}",     label: "Nom locataire" },
  { key: "{tenant_email}",    label: "Email locataire" },
  { key: "{landlord_name}",   label: "Nom propriétaire" },
  { key: "{landlord_email}",  label: "Email propriétaire" },
  { key: "{lease_id}",        label: "ID du bail (8 caractères)" },
  { key: "{signed_date}",     label: "Date de signature (AAAA-MM-JJ)" },
  { key: "{signed_datetime}", label: "Date+heure de signature" },
  { key: "{today}",           label: "Aujourd'hui (AAAA-MM-JJ)" },
  { key: "{today_long}",      label: "Aujourd'hui (JJ/MM/AAAA)" },
];

function CustomTextEditor({ field, onUpdate }) {
  if (!field || field.type !== "text") return null;

  const insertPlaceholder = (ph) => {
    const newText = (field.custom_text || "") + ph;
    onUpdate({ custom_text: newText });
  };

  return (
    <div className="mt-5 pt-4 border-t border-amber-200" data-testid="custom-text-editor">
      <h3 className="text-[10px] uppercase font-bold text-amber-700 tracking-wider mb-2">
        Édition · Texte personnalisé
      </h3>
      <label className="block">
        <span className="text-[9px] text-slate-600 font-semibold uppercase">Texte (200 char max)</span>
        <textarea
          value={field.custom_text || ""}
          onChange={(e) => onUpdate({ custom_text: e.target.value })}
          maxLength={200}
          rows={3}
          placeholder="Ex: Signé électroniquement le {signed_datetime} · Bail #{lease_id}"
          className="cc-input mt-1 text-[11px] font-mono"
          data-testid="custom-text-input"
        />
      </label>

      <label className="block mt-2">
        <span className="text-[9px] text-slate-600 font-semibold uppercase">Taille police</span>
        <input
          type="number"
          min={5}
          max={36}
          value={field.font_size || 10}
          onChange={(e) => onUpdate({ font_size: Math.max(5, Math.min(36, Number(e.target.value) || 10)) })}
          className="cc-input mt-1 text-xs font-mono w-20"
          data-testid="custom-text-font-size"
        />
      </label>

      <div className="mt-3">
        <p className="text-[9px] font-semibold text-slate-600 uppercase mb-1">Placeholders disponibles</p>
        <div className="space-y-1">
          {PLACEHOLDERS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => insertPlaceholder(p.key)}
              className="w-full text-left flex items-center justify-between gap-2 px-2 py-1 rounded bg-amber-50 hover:bg-amber-100 text-[10px] border border-amber-200 transition"
              data-testid={`custom-text-placeholder-${p.key.replace(/[{}]/g, "")}`}
            >
              <code className="font-mono text-amber-800 text-[9px]">{p.key}</code>
              <span className="text-slate-600 text-[9px] italic">{p.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}


function PageImage({ templateId, pageNum, version, onLoad, imageRef }) {
  // Doit servir l'image via Authorization Bearer → on récupère via fetch puis blob URL
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let revokedUrl = null;
    (async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const url = `${import.meta.env.VITE_BACKEND_URL || process.env.REACT_APP_BACKEND_URL}/api/admin/pdf-templates/${templateId}/page/${pageNum}`;
        const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) throw new Error("Erreur");
        const blob = await r.blob();
        const objUrl = URL.createObjectURL(blob);
        revokedUrl = objUrl;
        if (!cancelled) setSrc(objUrl);
      } catch (_e) {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (revokedUrl) URL.revokeObjectURL(revokedUrl);
    };
  }, [templateId, pageNum, version]);

  if (loading || !src) {
    return <div className="absolute inset-0 flex items-center justify-center bg-slate-100"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }
  return (
    <img
      ref={imageRef}
      src={src}
      alt={`Page ${pageNum}`}
      className="block w-full h-full select-none pointer-events-none"
      onLoad={(e) => onLoad({ width: e.target.naturalWidth, height: e.target.naturalHeight })}
      draggable={false}
    />
  );
}
